/**
 * The dated-field editor (1.Y deliverable 2): in the fields dialog, a temporal field's value now
 * with "Since <date>", or "None now"; "Change" (a scalar, `doc_ref`), "Add" and per value "End"
 * (`doc_refs`) open a form with the value and a "Since" in-world date (NOW by default); Save runs
 * `planSince` through `runSteps`. "History" lists every value, read-only. Only `GET` on open; a
 * reference or trashed page shows its values and no control.
 */
import { ApiError, type DocHitV1, type FieldDefV1, type LinkV1 } from '@codex/contracts';
import { Button, Icon, Skeleton, useLayer } from '@codex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useMemo, useRef, useState } from 'react';
import { type PropInput, toPropValue } from '../types/model/values.js';
import { useWhenContext, type WhenContextValue } from '../when/calendar-context.js';
import { WhenInput, type WhenInputState } from '../when/when-input.js';
import { refusalMessage, whenToBoundary } from '../when/when-model.js';
import { temporalKeys, useTemporalApi } from './api.js';
import { runSteps, StepsFailedError } from './commit.js';
import {
  currentValues,
  type DatedChange,
  type DatedRow,
  type DatedTarget,
  fromTextOf,
  historyRows,
  isRefField,
  otherEndOf,
  periodText,
  planSince,
  toTextOf,
  valueText,
} from './dated-model.js';
import { useTemporalEvents } from './events.js';
import { HistoryPopover } from './history-popover.js';
import { RefPicker } from './ref-picker.js';
import {
  ADD,
  addLabel,
  CANCEL,
  CHANGE,
  CONFLICT,
  changeLabel,
  END,
  endLabel,
  NONE_NOW,
  NOW_UNSET_NOTICE,
  NOW_UNSET_SINCE,
  SAVE,
  SINCE_LABEL,
  UNTITLED,
  updatedNotice,
  VALUES_FAILED,
} from './words.js';
import '../when/when.css';
import './temporal.css';

export interface ValueInputProps {
  /** What the GM has entered so far; null before anything. */
  readonly value: PropInput | null;
  readonly onChange: (input: PropInput) => void;
}

/** 1.B-2's editor for the field's kind family, writing into the form instead of the page. */
export type RenderValueInput = (props: ValueInputProps) => ReactNode;

export interface DatedFieldEditorProps {
  readonly docId: string;
  readonly field: FieldDefV1;
  readonly renderValueInput: RenderValueInput;
  /** A reference or trashed page: values only, no control. */
  readonly readOnly?: boolean;
}

type FormMode =
  | { readonly kind: 'change' }
  | { readonly kind: 'add' }
  | { readonly kind: 'end'; readonly link: LinkV1 };

const isConflict = (error: unknown) => error instanceof ApiError && error.code === 'row_conflict';

/** The field's rows: its states, or its links of the field's link type in its direction. */
function useFieldRows(docId: string, field: FieldDefV1) {
  const services = useTemporalApi();
  const isRef = isRefField(field);
  const linkTypesQ = useQuery({
    queryKey: temporalKeys.linkTypes,
    queryFn: () => services.linkTypes(),
    enabled: isRef,
    retry: false,
    staleTime: 60_000,
  });
  const linkType = isRef
    ? ((linkTypesQ.data ?? []).find((t) => t.id === field.linkTypeId) ?? null)
    : null;
  const direction = 'direction' in field.config ? field.config.direction : 'out';
  const rowsQ = useQuery({
    queryKey: isRef ? [...temporalKeys.links(docId), field.id] : temporalKeys.states(docId),
    queryFn: async (): Promise<DatedRow[]> => {
      if (linkType === null) return services.listStates(docId);
      const links = await services.listLinks(
        docId,
        linkType.symmetric
          ? { linkTypeKey: linkType.key }
          : { linkTypeKey: linkType.key, direction },
      );
      return links.filter(
        (link) =>
          link.linkTypeId === linkType.id &&
          (linkType.symmetric ||
            (direction === 'out' ? link.sourceId === docId : link.targetId === docId)),
      );
    },
    enabled: !isRef || linkType !== null,
    retry: false,
  });
  const failed =
    rowsQ.isError || linkTypesQ.isError || (isRef && linkTypesQ.isSuccess && linkType === null);
  const refetch = () => {
    void linkTypesQ.refetch();
    void rowsQ.refetch();
  };
  return { linkType, rows: rowsQ.data ?? null, failed, refetch };
}

export function DatedFieldEditor({
  docId,
  field,
  renderValueInput,
  readOnly = false,
}: DatedFieldEditorProps) {
  const services = useTemporalApi();
  const client = useQueryClient();
  const ctx = useWhenContext();
  useTemporalEvents(docId);
  const labelId = useId();
  const { linkType, rows, failed, refetch } = useFieldRows(docId, field);
  const [form, setForm] = useState<FormMode | null>(null);
  const [notice, setNotice] = useState('');
  const isRef = isRefField(field);
  const calendar = ctx.calendar;

  const otherIds = useMemo(
    () =>
      isRef && rows ? [...new Set(rows.map((r) => otherEndOf(r as LinkV1, docId)))].sort() : [],
    [isRef, rows, docId],
  );
  const titlesQ = useQuery({
    queryKey: temporalKeys.titles(otherIds),
    queryFn: () => services.titles(otherIds),
    enabled: otherIds.length > 0,
    retry: false,
  });
  const titles = useMemo(
    () => new Map((titlesQ.data ?? []).map((t) => [t.id, t.title ?? UNTITLED] as const)),
    [titlesQ.data],
  );

  if (failed) {
    return (
      <div className="cx-pills__field cx-temporal" data-testid="temporal.field">
        <span className="cx-pills__label">{field.label}</span>
        <span className="cx-pills__hint" role="alert">
          <Icon name="warning" size={14} /> {VALUES_FAILED}
        </span>
        <Button size="sm" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }
  if (rows === null || ctx.status === 'loading') {
    return (
      <div className="cx-pills__field cx-temporal">
        <span className="cx-pills__label">{field.label}</span>
        <Skeleton lines={1} label={`Loading ${field.label}`} />
      </div>
    );
  }

  const shown = currentValues(rows, field, ctx.now, calendar);
  const own = rows.filter((r) => isRef || ('fieldDefId' in r && r.fieldDefId === field.id));
  const history = historyRows(own, calendar, titles, docId);
  const shownValue = (row: DatedRow) =>
    'linkTypeId' in row ? (titles.get(otherEndOf(row, docId)) ?? UNTITLED) : valueText(row.value);
  const editable = !readOnly && ctx.status === 'ready';
  const target: DatedTarget = { docId, field, linkType };
  const done = async () => {
    setForm(null);
    setNotice(updatedNotice(field.label));
    await client.invalidateQueries({
      queryKey: isRef ? temporalKeys.links(docId) : temporalKeys.states(docId),
    });
  };

  return (
    <div
      className="cx-pills__field cx-temporal"
      data-testid="temporal.field"
      data-field-id={field.id}
    >
      <span className="cx-pills__label" id={labelId}>
        {field.label}
      </span>
      {shown.nowUnset && (
        <p className="cx-temporal__notice">
          <Icon name="info" size={14} /> {NOW_UNSET_NOTICE}
        </p>
      )}
      {shown.rows.length === 0 ? (
        <p className="cx-temporal__hint" data-testid="temporal.none">
          {NONE_NOW}
        </p>
      ) : (
        <ul className="cx-temporal__rows" aria-labelledby={labelId}>
          {shown.rows.map((row) => (
            <li key={row.id} className="cx-temporal__row" data-testid="temporal.value">
              <span className="cx-temporal__value">{shownValue(row)}</span>
              <span className="cx-temporal__hint">
                {periodText(fromTextOf(row, calendar), toTextOf(row, calendar))}
              </span>
              {editable &&
                field.kind === 'doc_refs' &&
                'linkTypeId' in row &&
                row.validTo === null &&
                form === null && (
                  <Button
                    size="sm"
                    aria-label={endLabel(shownValue(row))}
                    onClick={() => setForm({ kind: 'end', link: row })}
                  >
                    {END}
                  </Button>
                )}
            </li>
          ))}
        </ul>
      )}
      <div className="cx-temporal__actions">
        {editable && form === null && (
          <Button
            size="sm"
            onClick={() => {
              setNotice('');
              setForm({ kind: field.kind === 'doc_refs' ? 'add' : 'change' });
            }}
          >
            {field.kind === 'doc_refs' ? ADD : CHANGE}
          </Button>
        )}
        <HistoryPopover label={field.label} rows={history} />
      </div>
      {editable && form !== null && (
        <ChangeForm
          mode={form}
          target={target}
          rows={own}
          current={shown.nowUnset ? null : shown.rows}
          ctx={ctx}
          renderValueInput={renderValueInput}
          exclude={[docId, ...otherIds]}
          titleOf={shownValue}
          onDone={done}
          onConflict={() =>
            void client.invalidateQueries({
              queryKey: isRef ? temporalKeys.links(docId) : temporalKeys.states(docId),
            })
          }
          onCancel={() => setForm(null)}
        />
      )}
      <p className="cx-temporal__hint" role="status">
        {notice}
      </p>
    </div>
  );
}

interface ChangeFormProps {
  readonly mode: FormMode;
  readonly target: DatedTarget;
  /** Every row of the field. */
  readonly rows: readonly DatedRow[];
  /** The rows shown at NOW; null while NOW is unset. */
  readonly current: readonly DatedRow[] | null;
  readonly ctx: WhenContextValue;
  readonly renderValueInput: RenderValueInput;
  readonly exclude: readonly string[];
  readonly titleOf: (row: DatedRow) => string;
  readonly onDone: () => Promise<void>;
  readonly onConflict: () => void;
  readonly onCancel: () => void;
}

function ChangeForm({
  mode,
  target,
  rows,
  current,
  ctx,
  renderValueInput,
  exclude,
  titleOf,
  onDone,
  onConflict,
  onCancel,
}: ChangeFormProps) {
  const services = useTemporalApi();
  const { field, docId } = target;
  const [value, setValue] = useState<PropInput | null>(null);
  const [other, setOther] = useState<DocHitV1 | null>(null);
  const [when, setWhen] = useState<WhenInputState | null>(null);
  const [busy, setBusy] = useState(false);
  /** A write is under way: a second Enter before the re-render sends nothing. */
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  useLayer(!busy, onCancel, 'temporal-form');
  const calendar = ctx.calendar;
  const isRef = isRefField(field);

  const since =
    when?.read && when.ready && calendar
      ? whenToBoundary(when.read, { ...when.choice, calendar })
      : null;
  const stateValue = value === null ? null : toPropValue(field, value);
  const hasValue = mode.kind === 'end' || (isRef ? other !== null : stateValue !== null);
  const ready = since?.ok === true && hasValue && !busy;

  const label =
    mode.kind === 'end'
      ? endLabel(titleOf(mode.link))
      : mode.kind === 'add'
        ? addLabel(field.label)
        : changeLabel(field.label);

  const save = async () => {
    // Enter in Since reaches here too: nothing is sent unless Save itself could be pressed.
    if (!ready || inFlight.current || !since?.ok || !when?.read) return;
    setError(null);
    const earliest = when.read.preview.earliest ?? null;
    const change: DatedChange =
      mode.kind === 'end'
        ? { kind: 'end', link: mode.link }
        : !isRef
          ? { kind: 'set', value: stateValue as Exclude<typeof stateValue, null> }
          : field.kind === 'doc_ref'
            ? { kind: 'point', otherId: (other as DocHitV1).id }
            : { kind: 'add', otherId: (other as DocHitV1).id };
    const plan = planSince(
      target,
      rows,
      change,
      { boundary: since.boundary, earliest },
      calendar,
      current,
    );
    if ('refusal' in plan) {
      setError(plan.refusal);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      await runSteps(plan.steps, services);
      inFlight.current = false;
      setBusy(false);
      await onDone();
    } catch (failure) {
      inFlight.current = false;
      setBusy(false);
      const cause = failure instanceof StepsFailedError ? failure.cause : failure;
      if (isConflict(cause)) {
        setError(CONFLICT);
        onConflict();
      } else setError(refusalMessage(cause).message);
    }
  };

  return (
    <fieldset
      className="cx-temporal__form"
      data-testid="temporal.form"
      aria-busy={busy || undefined}
    >
      <legend className="cx-temporal__legend">{label}</legend>
      {mode.kind !== 'end' &&
        (isRef ? (
          <RefPicker chosen={other} onChoose={setOther} exclude={exclude} />
        ) : (
          renderValueInput({ value, onChange: setValue })
        ))}
      <WhenInput
        docId={docId}
        label={SINCE_LABEL}
        defaultNow={ctx.nowState === 'set'}
        allow={['point', 'now', 'relative']}
        onRead={setWhen}
        onSubmit={() => void save()}
      />
      {ctx.nowState !== 'set' && (
        <p className="cx-temporal__hint" data-testid="temporal.now-unset">
          <Icon name="info" size={14} /> {NOW_UNSET_SINCE}
        </p>
      )}
      {since?.ok === false && <p className="cx-temporal__hint">{since.reason}</p>}
      {error !== null && (
        <p className="cx-temporal__error" role="alert">
          <Icon name="warning" size={14} /> {error}
        </p>
      )}
      <div className="cx-temporal__actions">
        <Button
          variant="primary"
          size="sm"
          disabled={!ready}
          busy={busy}
          onClick={() => void save()}
        >
          {SAVE}
        </Button>
        <Button size="sm" disabled={busy} onClick={onCancel}>
          {CANCEL}
        </Button>
      </div>
    </fieldset>
  );
}
